import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  TruckIcon,
  CalendarIcon,
  MoneyIcon,
  GearIcon,
  InfoIcon,
  UsersIcon,
  DeviceMobileIcon,
  ArrowsClockwiseIcon,
  MapPinIcon,
} from '@phosphor-icons/react';

import { useAuth } from '../../contexts/auth-context';
import { useTranslation } from '../../contexts/translation-context';
import { Sidebar } from '../../components/Sidebar';
import { SidebarNavItem, SidebarUserItem, type ISidebarNavItemProps } from '../../components/sidebar/SidebarNavItem';
import { Button } from '../../components/button/Button';
import { useCarrierPortalSupplier } from './CarrierPortalCarrierContext';
import { SelfBillingMode } from 'src/types/enums';
import { ErrorStateBoundary } from '../../components/error-state/ErrorStateBoundary';

export interface ICarrierPortalDashboardProps {
  children: React.ReactNode;
}

type NavItem = Pick<ISidebarNavItemProps, 'to' | 'label' | 'icon'>;

export const CarrierPortalDashboard: React.FC<ICarrierPortalDashboardProps> = (props) => {
  const { children } = props;
  const { me, logout } = useAuth();
  const { supplier } = useCarrierPortalSupplier();
  const { i18n, lang } = useTranslation();
  const isDispatcher = me.carriers.some((c) => c.role === 'CarrierDispatcher');
  const isDriver = me.carriers.some((c) => c.role === 'CarrierDriver');

  if (isDriver) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center content-bg">
        <div className="flex max-w-md flex-col items-center gap-4 p-8 text-center">
          <DeviceMobileIcon className="h-12 w-12 text-fg-muted" />
          <h1 className="heading-one">{i18n('carrierPortal.driverBlock.title')}</h1>
          <p className="text-sm text-fg-secondary">{i18n('carrierPortal.driverBlock.message')}</p>
          <Button color="secondary" onTrigger={logout}>
            {i18n('myProfile.logout')}
          </Button>
        </div>
      </div>
    );
  }

  const [navigationOne, navigationTwo] = useMemo(() => {
    const navOne: NavItem[] = [];

    navOne.push(
      { label: i18n('carrierPortal.planning.planning'), to: './planning', icon: CalendarIcon },
      { label: i18n('carrierPortal.orders.orders'), to: './orders', icon: TruckIcon },
      { label: i18n('carrierPortal.liveTracking.liveTracking'), to: './tracking', icon: MapPinIcon },
    );

    // Only show self-billing for dispatchers of a carrier that gets, or has got, ride overviews or self-bills
    if (isDispatcher && (supplier.selfBillingMode !== SelfBillingMode.None || supplier.hasSelfBills)) {
      navOne.push({
        label: i18n(
          supplier.selfBillingMode === SelfBillingMode.SelfBilling
            ? 'carrierPortal.selfBilling.selfBilling'
            : 'carrierPortal.selfBilling.rideOverview',
        ),
        to: './self-billing',
        icon: MoneyIcon,
      });
    }

    const navTwo: NavItem[] = [
      {
        label: i18n('carrierPortal.generalInformation.generalInformation'),
        to: './knowledge-base',
        icon: InfoIcon,
      },
    ];

    // Only show business information and users for dispatchers
    if (isDispatcher) {
      navTwo.push(
        {
          label: i18n('carrierPortal.businessInformation.businessInformation'),
          to: './business-information/info',
          icon: GearIcon,
        },
        {
          label: i18n('carrierPortal.users.users'),
          to: './users',
          icon: UsersIcon,
        },
      );
    }

    return [navOne, navTwo];
  }, [i18n, lang, isDispatcher, supplier.selfBillingMode, supplier.hasSelfBills]);

  return (
    <div className="flex min-h-screen w-full">
      <div className="shrink-0">
        <Sidebar
          homeLink="."
          topItems={
            <>
              {me.carriers.length > 1 && (
                <div className="px-3 pb-2">
                  <Link
                    to="../select"
                    className="flex items-center gap-1.5 rounded-md border border-brand-border bg-brand-subtle px-2.5 py-1.5 text-xs font-medium text-brand-text transition-colors hover:bg-warning-subtle"
                  >
                    <span className="flex-1 truncate">{supplier.name}</span>
                    <ArrowsClockwiseIcon className="h-3 w-3 shrink-0" />
                  </Link>
                </div>
              )}

              <nav className="sidebar-nav">
                {navigationOne.map((item) => (
                  <SidebarNavItem key={item.to} {...item} />
                ))}
              </nav>

              <div className="sidebar-divider" />

              <nav className="sidebar-nav">
                {navigationTwo.map((item) => (
                  <SidebarNavItem key={item.to} {...item} />
                ))}
              </nav>
            </>
          }
          bottomItems={
            <nav className="sidebar-nav">
              <SidebarUserItem to="me" name={me.name} imageUrl={me.image} meta={supplier.name} />
            </nav>
          }
        />
      </div>

      <main className="pb-32 content-bg min-h-screen flex-1 min-w-0">
        <ErrorStateBoundary variant="page">{children}</ErrorStateBoundary>
      </main>
    </div>
  );
};
